<template>
  <div class="register-form" :class="{ 'register-form--embedded': embedded }">
    <header v-if="showBrand" class="form-heading">
      <div class="brand">
        <img src="@/assets/DiCloudAccessLogo.svg" alt="DiCloudAccess" class="brand-logo">
        <div class="brand-text">
          <span class="brand-en">DiCloudAccess</span>
          <span class="brand-zh">点云通</span>
        </div>
      </div>
      <h2 class="form-title">{{ title }}</h2>
    </header>
    <h2 v-else-if="showTitle" class="form-title">{{ title }}</h2>
    <p class="form-description">请填写以下信息，我们将尽快处理您的申请。</p>
    <el-form :model="form" :rules="rules" ref="formRef" label-position="top">
      <el-form-item label="姓名" prop="name">
        <el-input v-model="form.name" placeholder="请输入您的姓名"></el-input>
      </el-form-item>
      <el-form-item label="公司名称" prop="company_name">
        <el-input v-model="form.company_name" placeholder="请输入您的公司名称"></el-input>
      </el-form-item>
      <el-form-item label="邮箱" prop="contact">
        <el-input v-model="form.contact" placeholder="请输入您的邮箱地址"></el-input>
      </el-form-item>
      <el-form-item label="行业" prop="industry">
        <el-select v-model="form.industry" placeholder="请选择您的行业">
          <el-option label="测绘" value="surveying"></el-option>
          <el-option label="建筑" value="architecture"></el-option>
          <el-option label="海洋工程" value="smart_city"></el-option>
          <el-option label="其他" value="other"></el-option>
        </el-select>
      </el-form-item>
      <el-form-item label="验证码" prop="captcha">
        <div class="captcha-container">
          <el-input v-model="form.captcha" placeholder="请输入验证码"></el-input>
          <el-image :src="captchaUrl" @click="refreshCaptcha" class="captcha-image">
            <template #error>
              <div class="image-slot">
                <el-icon><icon-picture /></el-icon>
              </div>
            </template>
          </el-image>
        </div>
      </el-form-item>
      <el-form-item label="试用者声明" prop="declaration">
        <el-checkbox v-model="form.declaration">
          我已阅读并同意<el-link type="primary" :underline="false" @click="showTerms">《试用条款》</el-link>，承诺合法使用软件并保护知识产权。
        </el-checkbox>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="submitForm" :loading="loading">提交申请</el-button>
      </el-form-item>
    </el-form>
    <div class="form-reminder">
      <i class="el-icon-info"></i>
      申请提交后，我们将通过邮件将授权码发送给您，请注意查收。
    </div>
  </div>
</template>

<script setup>
import { ref, reactive } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Picture as IconPicture } from '@element-plus/icons-vue'
import authApi from '@/js/connect';  // 确保这行导入是正确的
import { defineEmits, defineProps } from 'vue';

const props = defineProps({
  showTitle: {
    type: Boolean,
    default: true
  },
  title: {
    type: String,
    default: '软件试用申请'
  },
  showBrand: {
    type: Boolean,
    default: false
  },
  embedded: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['registration-success']);

const formRef = ref(null);
const loading = ref(false);
const captchaUrl = ref('');

const form = reactive({
  name: '',
  contact: '', // 改为 contact 而不是 email
  company_name: '', // 改为 company_name 而不是 company
  industry: '',
  captcha: '',
  declaration: false,
  session_id: '' // 使用 snake_case
});

const rules = {
  name: [{ required: true, message: '请输入姓名', trigger: 'blur' }],
  contact: [
    { required: true, message: '请输入邮箱地址', trigger: 'blur' },
    { type: 'email', message: '请输入正确的邮箱地址', trigger: ['blur', 'change'] }
  ],
  company_name: [{ required: true, message: '请输入公司名称', trigger: 'blur' }],
  industry: [{ required: true, message: '请选择行业', trigger: 'change' }],
  captcha: [
    { required: true, message: '请输入验证码', trigger: 'blur' },
    { min: 4, max: 4, message: '验证码长度为4位', trigger: 'blur' }
  ],
  declaration: [{
    validator: (_rule, value, callback) => {
      if (value) callback();
      else callback(new Error('请同意试用者声明'));
    },
    trigger: 'change'
  }]
};

const refreshCaptcha = async () => {
  try {
    const sessionId = Date.now().toString();
    captchaUrl.value = await authApi.getCaptcha(sessionId);
    form.session_id = sessionId;
    console.log("captchaUrl.value",captchaUrl.value)
  } catch (error) {
    console.error('获取验证码失败:', error);
    ElMessage.error('获取验证码失败，请稍后重试');
  }
};

const submitForm = async () => {
  try {
    await formRef.value.validate();
    loading.value = true;
   
    const registrationData = {
      name: form.name,
      contact: form.contact,
      company_name: form.company_name,
      industry: form.industry,
      captcha: form.captcha,
      session_id: form.session_id
    };
    
    const response = await authApi.register(registrationData);
    
    if (response.status === 200 && !response.data.error && response.data.status !== 'error') {
      ElMessage.success(response.data.message || '申请已提交，等待后台确认。');
      formRef.value.resetFields();
      refreshCaptcha();
      emit('registration-success');
    } else {
      ElMessage.error(response.data.message || '提交失败，请稍后重试');
    }
  } catch (error) {
    if (error.response && error.response.data && error.response.data.error) {
      ElMessage.error(error.response.data.error);
    } else {
      ElMessage.error('提交失败，请稍后重试');
    }
  } finally {
    loading.value = false;
  }
};

// 初始化时获取验证码
refreshCaptcha();

const showTerms = () => {
  ElMessageBox.alert(
    '1. 试用期间，用户仅可将软件用于非商业目的的评估和测试。\n' +
    '2. 用户不得复制、修改、反编译或传播软件及其相关文档。\n' +
    '3. 用户应保护软件相关的知识产权，不得泄露任何保密信息。\n' +
    '4. 试用期结束后，如未获得正式授权，用户应立即停止使用并删除所有相关软件。\n' +
    '5. 本公司保留随时终止试用的权利，且不承担任何因使用软件而导致的损失或损害。',
    '试用条款',
    {
      confirmButtonText: '我已阅读并同意',
      callback: (action) => {
        if (action === 'confirm') {
          form.declaration = true;
        }
      }
    }
  );
};
</script>

<style scoped>
.register-form {
  max-width: 500px;
  width: 100%;
  margin: 0 auto;
  padding: 28px;
  background: var(--dca-surface);
  border: 1px solid var(--dca-border);
  border-radius: var(--dca-radius-lg);
  box-shadow: var(--dca-shadow);
  color: var(--dca-text);
  box-sizing: border-box;
}

/* 嵌在对话框内：外框由弹窗提供，内边距仍与独立页一致 */
.register-form--embedded {
  border: none;
  box-shadow: none;
  background: transparent;
}

.form-heading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.brand {
  display: flex;
  align-items: center;
  gap: 8px;
}

.brand-logo {
  height: 24px;
}

.brand-text {
  display: flex;
  align-items: baseline;
  gap: 6px;
}

.brand-en {
  font-size: 0.9em;
  font-weight: var(--dca-fw-semibold);
  color: var(--dca-text-muted);
  letter-spacing: -0.01em;
}

.brand-zh {
  font-size: 0.82em;
  font-weight: var(--dca-fw-regular);
  color: var(--dca-text-faint);
}

.form-heading .form-title {
  margin: 0;
  text-align: center;
  font-size: 1.3em;
  line-height: 1.3;
}

.form-title {
  text-align: center;
  margin-bottom: 16px;
  color: var(--dca-text);
  font-size: 1.4em;
  font-weight: 600;
}

.form-description {
  text-align: center;
  margin-bottom: 20px;
  font-size: 14px;
  color: var(--dca-text-muted);
}

.register-form :deep(.el-form-item) {
  margin-bottom: 18px;
}

/* 轻量化 label：降低字重 */
.register-form :deep(.el-form-item__label) {
  color: var(--dca-text-muted);
  font-weight: 400;
  font-size: 0.92em;
  margin-bottom: 4px;
}

/* Element Plus 输入框结构为 wrapper + inner，需用 :deep 命中 */
.register-form :deep(.el-input__wrapper),
.register-form :deep(.el-textarea__inner) {
  background-color: var(--dca-surface-2);
  box-shadow: 0 0 0 1px var(--dca-border) inset;
}

.register-form :deep(.el-input__wrapper.is-focus) {
  box-shadow: 0 0 0 1px var(--dca-accent) inset;
}

.register-form :deep(.el-input__inner),
.register-form :deep(.el-textarea__inner) {
  color: var(--dca-text);
  font-size: 0.95em;
}

.register-form :deep(.el-input__inner::placeholder) {
  color: var(--dca-text-faint);
}

.register-form :deep(.el-checkbox__label) {
  color: var(--dca-text-muted);
  line-height: 1.4;
  font-size: 0.9em;
  display: inline;
  vertical-align: middle;
  white-space: normal;
}

.register-form :deep(.el-checkbox) {
  display: flex;
  align-items: flex-start;
  margin-right: 0;
  width: 100%;
  height: auto;
}

.register-form :deep(.el-button) {
  width: 100%;
  font-size: 1em;
  padding: 10px 15px;
  font-weight: 600;
  height: 42px;
}

.form-reminder {
  margin-top: 15px;
  padding: 10px 12px;
  background-color: var(--dca-accent-soft);
  border-radius: var(--dca-radius);
  font-size: 0.85em;
  display: flex;
  align-items: flex-start;
  border: 1px solid var(--dca-border);
  color: var(--dca-text-muted);
}

.form-reminder i {
  margin-right: 8px;
  font-size: 1.2em;
  color: #ffd04b;
  flex-shrink: 0;
  margin-top: 2px;
}

.el-link {
  font-weight: bold;
  margin: 0 2px;
  display: inline;
  white-space: normal;
  word-break: break-word;
}

@media (max-width: 768px) {
  .register-form {
    padding: 20px;
    margin: 0;
    border-radius: 0;
  }

  .register-form--embedded {
    padding: 16px 16px 12px;
  }

  .register-form--embedded .form-description {
    margin-bottom: 8px;
  }

  .register-form--embedded :deep(.el-form-item) {
    margin-bottom: 8px;
  }

  .register-form--embedded :deep(.el-form-item:last-child) {
    margin-bottom: 0;
  }

  .register-form--embedded .form-reminder {
    margin-top: 12px;
  }

  h2 {
    font-size: 1.8em;
    margin-bottom: 20px;
  }

  .el-form-item {
    margin-bottom: 15px;
  }

  .el-form-item__label {
    font-size: 0.95em !important;
    margin-bottom: 4px !important;
  }

  .el-input__inner,
  .el-select .el-input__inner,
  .el-button {
    height: 36px;
    font-size: 0.9em;
  }

  .el-checkbox__label {
    font-size: 0.85em;
  }

  .form-reminder {
    font-size: 0.8em;
    padding: 8px;
  }
}

@media (max-width: 480px) {
  .register-form {
    padding: 16px;
  }

  h2 {
    font-size: 1.5em;
    margin-bottom: 15px;
  }

  .el-form-item__label {
    font-size: 0.85em !important;
  }

  .el-input__inner,
  .el-select .el-input__inner,
  .el-button {
    height: 32px;
    font-size: 0.8em;
  }

  .el-checkbox__label {
    font-size: 0.75em;
  }

  .form-reminder {
    font-size: 0.7em;
    padding: 6px;
  }
}

@media (max-width: 320px) {
  .register-form {
    padding: 12px;
  }

  h2 {
    font-size: 1.3em;
    margin-bottom: 10px;
  }

  .el-form-item__label {
    font-size: 0.8em !important;
  }

  .el-input__inner,
  .el-select .el-input__inner,
  .el-button {
    height: 30px;
    font-size: 0.75em;
  }

  .el-checkbox__label {
    font-size: 0.7em;
  }

  .form-reminder {
    font-size: 0.65em;
    padding: 5px;
  }
}

.captcha-container {
  display: flex;
  align-items: center;
}

.captcha-image {
  margin-left: 10px;
  width: 100px;
  height: 40px;
  cursor: pointer;
}

.image-slot {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background: var(--dca-surface-2);
  color: var(--dca-text-faint);
  font-size: 30px;
}

@media (max-width: 480px) {
  .captcha-container {
    flex-direction: column;
    align-items: flex-start;
  }

  .captcha-image {
    margin-left: 0;
    margin-top: 10px;
  }
}

@media (min-width: 769px) {
  .el-form-item__content {
    display: flex;
    align-items: center;
  }

  .el-checkbox {
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
  }

  .el-checkbox__label {
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

@media (max-width: 768px) {
  .el-checkbox__label {
    white-space: normal;
    word-break: break-word;
  }
}
</style>